import type { CSSProperties } from 'react';

/**
 * Design tokens for the big-screen display surfaces (/events/:id/display).
 *
 * The defaults are the Destination Tours slide-deck language — navy stage,
 * paper text, map-pin green accent — so an event with no custom branding
 * still looks fully branded. Event branding fields override individual
 * tokens: `backgroundColor` replaces the navy stage, `accentColor` replaces
 * the pin green, `logoUrl` joins the header/frame, `customPointsLabel`
 * replaces "pts".
 */

export const DECK = {
  paper: '#f8f7f4',
  ink: '#0f172a',
  inkSoft: '#475569',
  mist: '#e7e5e0',
  navy: '#1e293b',
  navySoft: '#334155',
  pin: '#22c55e',
  /** Pin green darkened for text on paper (deck uses green-700). */
  pinInk: '#15803d',
} as const;

export interface DisplayBranding {
  backgroundColor?: string | null;
  accentColor?: string | null;
  logoUrl?: string | null;
  customPointsLabel?: string | null;
}

export type DisplayTone = 'navy' | 'paper';

/**
 * CSS custom properties consumed by every display component. Set once on
 * the display root so children style themselves with `var(--dsp-*)`.
 *
 * - `--dsp-bg`       stage background (navy / paper / branded color)
 * - `--dsp-surface`  card surface lifted off the stage
 * - `--dsp-line`     hairline borders on the stage
 * - `--dsp-text`     primary text
 * - `--dsp-soft`     secondary text
 * - `--dsp-accent`   accent for fills/strokes (pin green or branded)
 * - `--dsp-accent-text` accent usable as text on the current stage
 */
export function displayVars(branding: DisplayBranding, tone: DisplayTone): CSSProperties {
  const accent = branding.accentColor || DECK.pin;

  if (tone === 'paper') {
    return {
      '--dsp-bg': DECK.paper,
      '--dsp-surface': '#ffffff',
      '--dsp-line': DECK.mist,
      '--dsp-text': DECK.ink,
      '--dsp-soft': DECK.inkSoft,
      '--dsp-accent': accent,
      '--dsp-accent-text': branding.accentColor || DECK.pinInk,
    } as CSSProperties;
  }

  const bg = branding.backgroundColor || DECK.navy;
  return {
    '--dsp-bg': bg,
    // Lift surfaces off any branded background without hardcoding navy-soft.
    '--dsp-surface': `color-mix(in srgb, ${DECK.paper} 7%, ${bg})`,
    '--dsp-line': 'rgba(248, 247, 244, 0.12)',
    '--dsp-text': DECK.paper,
    '--dsp-soft': 'rgba(248, 247, 244, 0.6)',
    '--dsp-accent': accent,
    '--dsp-accent-text': accent,
  } as CSSProperties;
}

export function pointsLabel(branding: DisplayBranding): string {
  return branding.customPointsLabel?.trim() || 'pts';
}

/**
 * The deck's faint city-grid texture, rendered on navy-tone stages.
 * Returns background style for an absolutely-positioned overlay div.
 */
export const GRID_TEXTURE: CSSProperties = {
  backgroundImage:
    'linear-gradient(rgba(248,247,244,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(248,247,244,0.5) 1px, transparent 1px)',
  backgroundSize: '56px 56px',
  opacity: 0.05,
};
